/**
 * The targets an organization shares with its members (`policy.sharedTargets`
 * in its settings; owner's decision Q10 of Claude Design « Proposition 26
 * octobre - Profils d'environnement »: « une target partagée dont un secret
 * manque sur cette machine est proposée inerte avec sa raison, pas cachée »).
 *
 * A shared target is a workspace target (`settings/targets.ts`) cut to what an
 * organization can say for every one of its workspaces: its name, how it is
 * reached, the Production badge, the hosts it adds and the secrets it swaps in
 * — by NAME only. A value is never in this document, which every member reads:
 * each member types it on their own computer, where it is sealed by the
 * keychain beside the workspace's own target secrets. No project variables
 * (a target's `env` names a workspace's projects) and no sources here.
 *
 * On a member's machine each one is added to the targets of the organization's
 * own workspaces (`sharedTargetsOf`, read by `resolveSettings`), locked, with
 * the organization's name (`sharedBy`).
 */
import { MAX_TARGETS, MAX_TARGET_NAME_CHARS, SECRET_NAME, normalizeTargets, targetProblems, targetSecretKey, type WorkspaceTarget } from '../settings/targets';
import { isPlainObject } from '../workspace/shape';
import { targetRuleWords } from './targets-policy';

export type OrgSharedTarget = Pick<WorkspaceTarget, 'id' | 'name' | 'production' | 'mode' | 'hosts' | 'secrets'>;

const FIELDS = ['id', 'name', 'production', 'mode', 'hosts', 'secrets'] as const;
const SHARED_ID = /^[A-Za-z0-9_-]{1,40}$/;

const cut = (one: WorkspaceTarget): OrgSharedTarget => ({
  id: one.id,
  name: one.name,
  production: one.production,
  mode: one.mode,
  hosts: one.hosts,
  secrets: one.secrets
});

/** As a stored document carries it: what is well formed is kept, the rest dropped — never a failure. */
export function sharedTargetsOf(raw: unknown): OrgSharedTarget[] {
  return normalizeTargets(raw).map(cut);
}

/** As an administrator wrote it — refused with a sentence per fault, never filtered. */
export function parseSharedTargets(input: unknown, problems: string[]): OrgSharedTarget[] {
  if (input === undefined || input === null) return [];
  const at = 'policy.sharedTargets';
  if (!Array.isArray(input)) {
    problems.push(`${at}: Expected a list.`);
    return [];
  }
  if (input.length > MAX_TARGETS) problems.push(`${at}: At most ${MAX_TARGETS} targets.`);
  const seen = new Set<string>();
  const names = new Set<string>();
  input.forEach((raw, i) => {
    const here = `${at}[${i}]`;
    if (!isPlainObject(raw)) {
      problems.push(`${here}: Expected an object.`);
      return;
    }
    for (const key of Object.keys(raw)) {
      if (!(FIELDS as readonly string[]).includes(key)) problems.push(`${here}.${key}: Not a field this version of Airlock knows.`);
    }
    if (typeof raw.id !== 'string' || !SHARED_ID.test(raw.id)) problems.push(`${here}.id: Expected letters, digits, dashes or underscores.`);
    else if (seen.has(raw.id)) problems.push(`${here}.id: ${raw.id} is listed twice.`);
    else seen.add(raw.id);
    const name = typeof raw.name === 'string' ? raw.name.trim() : '';
    if (!name) problems.push(`${here}.name: Name it — the Target chip lists it by that.`);
    else if (name.length > MAX_TARGET_NAME_CHARS) problems.push(`${here}.name: At most ${MAX_TARGET_NAME_CHARS} characters.`);
    else if (name.toLowerCase() === 'local') problems.push(`${here}.name: Local is the base — give this target another name.`);
    else if (names.has(name.toLowerCase())) problems.push(`${here}.name: Another target is named ${name}.`);
    else names.add(name.toLowerCase());
    if (raw.production !== undefined && typeof raw.production !== 'boolean') problems.push(`${here}.production: Expected true or false.`);
    if (raw.mode !== undefined && raw.mode !== 'direct' && raw.mode !== 'host') problems.push(`${here}.mode: Expected direct or host.`);
    if (raw.hosts !== undefined && (!Array.isArray(raw.hosts) || raw.hosts.some((h) => typeof h !== 'string'))) {
      problems.push(`${here}.hosts: Expected a list of hosts.`);
    }
    if (raw.secrets !== undefined && !Array.isArray(raw.secrets)) problems.push(`${here}.secrets: Expected a list.`);
    for (const [j, secret] of (Array.isArray(raw.secrets) ? raw.secrets : []).entries()) {
      const there = `${here}.secrets[${j}]`;
      if (!isPlainObject(secret)) {
        problems.push(`${there}: Expected an object.`);
        continue;
      }
      // A value is never shared: each member types it on their own computer.
      for (const key of Object.keys(secret)) {
        if (key !== 'name' && key !== 'hosts') {
          problems.push(
            key === 'value'
              ? `${there}.value: A secret’s value is never shared — each member sets it on their own computer.`
              : `${there}.${key}: Not a field this version of Airlock knows.`
          );
        }
      }
      if (typeof secret.name !== 'string' || !SECRET_NAME.test(secret.name)) {
        problems.push(`${there}.name: Letters, digits, dots, dashes and underscores, starting with a letter or a digit.`);
      }
    }
  });
  const kept = sharedTargetsOf(input);
  // Each one whole, as `normalizeTargets` would keep it: a host it dropped, a secret sent nowhere, said by name.
  for (const [i, raw] of input.entries()) {
    if (!isPlainObject(raw)) continue;
    const hosts = Array.isArray(raw.hosts) ? raw.hosts : [];
    const one = kept.find((t) => t.id === raw.id);
    if (!one) continue;
    for (const host of hosts) {
      if (typeof host === 'string' && !one.hosts.includes(host.trim().toLowerCase())) {
        problems.push(`policy.sharedTargets[${i}].hosts: ${host} is not a host name.`);
      }
    }
    const faults = targetProblems({ ...one, env: {}, sources: [], reviewResults: false }, []);
    for (const [secret, why] of Object.entries(faults.secrets)) problems.push(`policy.sharedTargets[${i}].secrets: ${secret}: ${why}`);
  }
  return kept;
}

/**
 * The shared targets as a member's machine adds them to one of the
 * organization's workspaces: a target like the workspace's own, with no
 * project variable and no source, marked `sharedBy` the organization's name.
 */
export function sharedAsWorkspaceTargets(org: string, shared: readonly OrgSharedTarget[]): WorkspaceTarget[] {
  return shared.map((one) => ({ ...one, env: {}, sources: [], reviewResults: false, sharedBy: org }));
}

/**
 * Why a shared target cannot be picked on this computer — one of its secrets
 * has no value here (Q10: offered inert with its reason, never hidden) — or
 * null. Every picker and the Supervisor's `targetRefusal` say the same.
 */
export function sharedSecretRefusal(target: Pick<WorkspaceTarget, 'id' | 'name' | 'secrets' | 'sharedBy'>, held: readonly string[]): string | null {
  if (!target.sharedBy) return null;
  const unset = target.secrets.find((secret) => !held.includes(targetSecretKey(target, secret.name)));
  return unset ? targetRuleWords.sharedSecret(target.name, unset.name) : null;
}
